import { Fragment, useEffect, useRef, useState } from 'react';
import { CheckSquare, FileText, FolderKanban, Search, StickyNote, TextQuote, X } from 'lucide-react';
import type { BuscaDto, ResultadoBuscaDto, TipoResultadoBusca } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';

const ICONES: Record<TipoResultadoBusca, typeof Search> = {
  processo: FolderKanban,
  atividade: CheckSquare,
  nota: StickyNote,
  documento: FileText,
  trecho: TextQuote,
};
const NOMES: Record<TipoResultadoBusca, string> = {
  processo: 'Licitações',
  atividade: 'Atividades',
  nota: 'Notas',
  documento: 'Documentos',
  trecho: 'No texto dos editais',
};
const ORDEM: TipoResultadoBusca[] = ['processo', 'trecho', 'atividade', 'nota', 'documento'];

/** Texto com os termos entre [[ e ]] (vindos do servidor) destacados, sem interpretar HTML. */
function Destaque({ texto }: { texto: string }) {
  return (
    <>
      {texto.split(/(\[\[.*?\]\])/g).map((parte, i) =>
        parte.startsWith('[[') ? <mark key={i}>{parte.slice(2, -2)}</mark> : <Fragment key={i}>{parte}</Fragment>,
      )}
    </>
  );
}

/** Espera a pessoa parar de digitar antes de buscar. */
function useBusca(termo: string, processoUuid: string | undefined) {
  const { espacos } = useDependencias();
  const [busca, definirBusca] = useState<BuscaDto | null>(null);
  const [carregando, definirCarregando] = useState(false);
  useEffect(() => {
    if (termo.trim().length < 2) return definirBusca(null);
    let valido = true;
    definirCarregando(true);
    const executar = async () => {
      const r = await espacos.buscar(termo, processoUuid).catch(() => ({ termo, resultados: [] }));
      if (!valido) return;
      definirBusca(r);
      definirCarregando(false);
    };
    const espera = setTimeout(() => void executar(), 250);
    return () => {
      valido = false;
      clearTimeout(espera);
    };
  }, [termo, processoUuid, espacos]);
  return { busca, carregando };
}

/**
 * Busca geral (Ctrl K). Na barra superior procura no quadro todo: licitações, atividades, notas, documentos e o texto
 * dos editais. Dentro da licitação (`processoUuid`) há um campo próprio que procura só no que é dela; com a licitação
 * aberta a barra fica bloqueada atrás da janela e o atalho vai para o campo da licitação.
 */
export function BuscaGeral({ processoUuid }: { processoUuid?: string }) {
  const { espacos } = useDependencias();
  const [termo, definirTermo] = useState('');
  const [aberta, definirAberta] = useState(false);
  const campo = useRef<HTMLInputElement>(null);
  const escopo = processoUuid;
  const { busca, carregando } = useBusca(termo, escopo);

  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if (campo.current?.closest('[inert]')) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        campo.current?.focus();
        definirAberta(true);
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, []);

  const abrir = (r: ResultadoBuscaDto) => {
    definirAberta(false);
    if (r.processoUuid) espacos.navegar({ ...espacos.atual, processoUuid: r.processoUuid });
  };

  return (
    <div className="busca-geral" onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && definirAberta(false)}>
      <label className="barra-superior__busca">
        <Search size={18} aria-hidden />
        <input
          ref={campo}
          value={termo}
          placeholder={escopo ? 'Buscar nesta licitação' : 'O que você precisa encontrar?'}
          aria-label="Buscar"
          onChange={(e) => {
            definirTermo(e.target.value);
            definirAberta(true);
          }}
          onFocus={() => definirAberta(true)}
          onKeyDown={(e) => e.key === 'Escape' && definirAberta(false)}
        />
        {termo ? (
          <button type="button" className="busca-geral__limpar" aria-label="Limpar a busca" onClick={() => definirTermo('')}>
            <X size={16} aria-hidden />
          </button>
        ) : (
          <kbd>Ctrl K</kbd>
        )}
      </label>
      {aberta && termo.trim().length >= 2 && (
        <PainelResultados busca={busca} carregando={carregando} escopoProcesso={Boolean(escopo)} podeAmpliar={false} ampliar={() => undefined} restringir={() => undefined} abrir={abrir} />
      )}
    </div>
  );
}

type PropsPainel = {
  busca: BuscaDto | null;
  carregando: boolean;
  escopoProcesso: boolean;
  podeAmpliar: boolean;
  ampliar: () => void;
  restringir: () => void;
  abrir: (r: ResultadoBuscaDto) => void;
};

function PainelResultados({ busca, carregando, escopoProcesso, podeAmpliar, ampliar, restringir, abrir }: PropsPainel) {
  const grupos = ORDEM.map((tipo) => ({ tipo, itens: busca?.resultados.filter((r) => r.tipo === tipo) ?? [] })).filter((g) => g.itens.length);
  return (
    <div className="busca-geral__painel" role="listbox" aria-label="Resultados da busca" tabIndex={-1}>
      {podeAmpliar && (
        <div className="busca-geral__escopo">
          <button type="button" aria-pressed={escopoProcesso} onClick={restringir}>
            Nesta licitação
          </button>
          <button type="button" aria-pressed={!escopoProcesso} onClick={ampliar}>
            No quadro todo
          </button>
        </div>
      )}
      {carregando && !busca && <p className="busca-geral__vazio">Buscando…</p>}
      {busca && grupos.length === 0 && <p className="busca-geral__vazio">Nada encontrado para “{busca.termo}”.</p>}
      {grupos.map(({ tipo, itens }) => {
        const Icone = ICONES[tipo];
        return (
          <section key={tipo} className="busca-geral__grupo">
            <h3>{NOMES[tipo]}</h3>
            {itens.map((r) => (
              <button key={`${r.tipo}-${r.alvoUuid}`} type="button" role="option" aria-selected={false} className="busca-geral__item" onClick={() => abrir(r)}>
                <Icone size={16} aria-hidden />
                <span>
                  <strong>
                    {r.titulo}
                    {r.pagina ? ` · p. ${r.pagina}` : ''}
                  </strong>
                  {r.processoNumero && r.tipo !== 'processo' && <em>{r.processoNumero}</em>}
                  <small>
                    <Destaque texto={r.trecho} />
                  </small>
                </span>
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}
